import { z } from 'zod';

/**
 * Контракт ЕСУТД, ГРУЗОВОЙ ЭПЛ. Спецтехника ездит по грузовому листу: пассажирская
 * цепочка требует `transportationType`/`carrierType` и госномер автобуса, которых у
 * экскаватора нет.
 *
 * Цепочка та же по форме — девять вызовов, шесть методов, — и все тела, кроме создания,
 * общие с пассажирским листом (`SignBody`, `MedicalExaminationBody`,
 * `TechnicalInspectionBody`, `ConfirmReadingBody`, `ReturnDateTimeBody`). Отличий два:
 * своё тело создания и отметка возврата на этапе `SECOND_TIME_CARRIER`, а не
 * `ORGANIZATION`.
 *
 * Профиль: канал A `POST /api/integration/action` + `X-API-KEY`, `signType=ESP`.
 *
 * Почему схемы НЕ `.strict()`: тела ЕСУТД — плоские Java-DTO, Spring Boot по умолчанию
 * игнорирует незнакомые поля. `.strict()` у нас означал бы, что добавление поля на их
 * стороне (или лишнее поле в снимке) роняет отправку на нашей границе без нужды.
 * Строгость нам нужна на СВОИХ полях: формат ИИН, кодов КАТО и времени.
 */

/**
 * Наличие груза, `cargoType`. `OWN` — груз перевозчика: именно этот случай у теплосетей,
 * техника возит собственный грунт, трубы и материалы, а не чужую партию по ТТН.
 */
export const esutdCargoTypeSchema = z.enum(['YES', 'NO', 'OWN']);
export type EsutdCargoType = z.infer<typeof esutdCargoTypeSchema>;

/**
 * Методы `EIntegrationMethodType`, относящиеся к нашему профилю.
 *
 * `LAUNCH_WAYBILL_CARGO` и `UPDATE_WAYBILL_CARGO` не нужны: лист создаётся сразу готовым
 * и правится только до создания. `EDIT_IN_PROGRESS_WAYBILL_CARGO` — правка выданного
 * документа, её в цепочке нет, изменения вносятся в веб-кабинете ЕСУТД.
 */
export const ESUTD_METHODS = [
  'CREATE_WAYBILL_CARGO',
  'SIGN_WAYBILL_CARGO',
  'SIGN_WAYBILL_CARGO_MEDICAL_EXAMINATION',
  'SIGN_WAYBILL_CARGO_TECHNICAL_INSPECTION',
  'CONFIRM_READING_WAYBILL_CARGO',
  'SET_RETURN_DATE_TIME_WAYBILL_CARGO',
  'REVOKE_WAYBILL_CARGO',
  'TAKE_TECHNICAL_RESPONSIBILITY_WAYBILL_CARGO',
  'ISSUE_SIGN_ENVELOPE',
] as const;
export type EsutdMethod = (typeof ESUTD_METHODS)[number];

/** Результат осмотра: `ETechnicalInspectionPreTrip` / `EMedicalExaminationPreTrip`. */
export const examResultSchema = z.enum(['YES', 'NO']);
export type EsutdExamResult = z.infer<typeof examResultSchema>;

/** `EWorkflowSignStatus`. DISAGREE аннулирует документ навсегда, это не «на доработку». */
export const esutdSignStatusSchema = z.enum(['AGREE', 'DISAGREE']);
export type EsutdSignStatus = z.infer<typeof esutdSignStatusSchema>;

// ─────────────────────────── примитивы ───────────────────────────

export const ESUTD_IIN_MESSAGE = 'ИИН должен содержать ровно 12 цифр';
export const ESUTD_BIN_MESSAGE = 'БИН должен содержать ровно 12 цифр';

/** Контрольный разряд ЕСУТД не считает — проверяем только формат. */
export const esutdIinSchema = z.string().regex(/^\d{12}$/, ESUTD_IIN_MESSAGE);
export const esutdBinSchema = z.string().regex(/^\d{12}$/, ESUTD_BIN_MESSAGE);

/**
 * `kato.code` не уникален: ровно один дубль — `101010000` («СЕМЕЙ» и «г.Семей»).
 * `findByCode` возвращает `Optional`, поэтому Spring Data на двух документах бросает
 * `IncorrectResultSizeDataAccessException` → детерминированные HTTP 500.
 * Отсекаем до отправки: маршрут через этот код ЕСУТД принять не может.
 */
export const ESUTD_KATO_DUPLICATE_CODES: readonly string[] = ['101010000'];
export const ESUTD_KATO_DUPLICATE_MESSAGE =
  'Код КАТО 101010000 (Семей) в справочнике ЕСУТД задублирован и вызывает ошибку 500 — ' +
  'используйте другой код или согласуйте маршрут с оператором ЕСУТД';
export const ESUTD_KATO_CODE_MESSAGE =
  'Код КАТО: 8–9 цифр либо 1–2 цифры (короткие коды упразднённых районов)';

/**
 * В фиде `kato.json` 18 610 кодов по 9 знаков, 2 по 8 (`10361112` с.Каскырбай 2 и
 * `62381117` зимовка Каратобе — живые записи уровня 6) и 24 короткие в 1–2 знака
 * (все с `isMarkedToDelete: true`). Десятизначных кодов нет.
 *
 * Восьмизначные обязаны проходить: ЕСУТД ищет `findByCode` точным совпадением и никакого
 * формата не навязывает, так что маршрут через эти два пункта на стенде валиден.
 */
export const esutdKatoCodeSchema = z
  .string()
  .regex(/^(\d{8,9}|\d{1,2})$/, ESUTD_KATO_CODE_MESSAGE)
  .refine((code) => !ESUTD_KATO_DUPLICATE_CODES.includes(code), ESUTD_KATO_DUPLICATE_MESSAGE);

export const ESUTD_ISO_DATE_TIME_MESSAGE =
  'Время для ЕСУТД: ISO-8601 с явным смещением (2026-08-11T09:00:00+05:00). ' +
  'Без смещения Jackson читает значение как UTC и время молча уезжает на 5 часов';

export const ESUTD_ISO_DATE_TIME_PATTERN =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,6})?(Z|[+-]\d{2}:\d{2})$/;

export const esutdIsoDateTimeSchema = z
  .string()
  .regex(ESUTD_ISO_DATE_TIME_PATTERN, ESUTD_ISO_DATE_TIME_MESSAGE);

/** id ЭПЛ на стороне ЕСУТД. Уходит в тело, а на канале B — в путь URL. */
export const esutdEntityIdSchema = z.string().trim().min(1, 'Не указан идентификатор ЭПЛ в ЕСУТД');

/**
 * Контейнер ЭЦП. `checkSignature` безусловно зовёт `verifyAll`, а тот падает
 * на пустом XML — подпись обязательна и в legacy-, и в каноническом режиме.
 */
export const esutdSignatureXmlSchema = z
  .string()
  .trim()
  .min(1, 'Пустой контейнер ЭЦП: сервер подписи не вернул подписанный XML');

/**
 * Госномер сверяется побайтово: `findByLicensePlate` без нормализации, поэтому регистр и
 * пробелы значимы, а кириллица ≠ латиница.
 *
 * Регистр НЕ навязываем: на `licensePlate` в справочнике ЕСУТД нет ничего кроме
 * `@NotBlank`, так что там законно лежит и «123abc02». Именно это значение сверка копирует
 * в `Machine.esutdPlateNumber` (совпадение ищется по upper-case ключу) и именно его надо
 * отправить обратно буква в букву. Прежний `[0-9A-Z]` отбивал такую единицу на НАШЕЙ
 * границе — лист не создавался вообще, хотя ЕСУТД принял бы запрос.
 */
export const esutdLicensePlateSchema = z
  .string()
  .regex(/^[0-9A-Za-z]{5,10}$/, 'Госномер для ЕСУТД: 5–10 символов, только латиница и цифры');

/** Одометр — `Long`, остатки топлива — `BigDecimal`; отрицательных значений не бывает. */
export const esutdOdometerSchema = z.number().int().nonnegative();
export const esutdFuelSchema = z.number().nonnegative();
